import { getKeyFromUrl } from '@helpers/assetUrls';
import {
  buildRealtimeEntitySnapshot,
  type IRealtimeEntity,
  type RealtimeEntityPropertyValue,
} from '@interfaces/realtimeEntities';
import SpawnedEntitiesService from '@services/SpawnedEntitiesService';

import {
  updateRealtimeEntityInUnreal,
  type SendMessageToUnreal,
} from './realtimeEntitiesUnreal';
import {
  collectRelocatedProperties,
  isUnsavedUploadUrl,
} from './fileProperties';

type PropertyBag = Record<string, RealtimeEntityPropertyValue>;

interface PersistRealtimeEntityParams {
  roomId: string;
  brandingId: string;
  /** The entity as it should be saved — name and all. */
  entity: IRealtimeEntity;
  sendMessageToUnreal: SendMessageToUnreal;
  upsertRealtimeEntity: (
    update: Partial<IRealtimeEntity> & { entityId: string },
  ) => void;
  /** Told which live values the save changed, so an open panel re-seeds them. */
  publishProperties?: (entityId: string, properties: PropertyBag) => void;
}

/**
 * Names the uploads the backend did not keep, so the hub stops showing a file
 * that is gone.
 *
 * @param sent - Property bag the save sent.
 * @param stored - Property bag the backend returned.
 * @returns Those property names, each mapped to the empty value.
 */
const collectDroppedUploads = (
  sent: PropertyBag,
  stored: PropertyBag | undefined,
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(sent)
      .filter(
        ([name, value]) =>
          typeof value === 'string' &&
          isUnsavedUploadUrl(value) &&
          stored?.[name] === undefined,
      )
      .map(([name]) => [name, '']),
  );

/**
 * Names the URLs that came back with the same key but a fresh signature; kept
 * as sent, they would read as an unsaved edit forever.
 *
 * @param sent - Property bag the save sent.
 * @param stored - Property bag the backend returned.
 * @returns The stored values to adopt, by property name.
 */
const collectResignedProperties = (
  sent: PropertyBag,
  stored: PropertyBag | undefined,
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(stored ?? {}).filter(([name, storedValue]) => {
      const sentValue = sent[name];

      return (
        typeof storedValue === 'string' &&
        typeof sentValue === 'string' &&
        storedValue !== sentValue &&
        getKeyFromUrl(storedValue) === getKeyFromUrl(sentValue)
      );
    }) as [string, string][],
  );

/**
 * Saves an entity's current state and adopts what the backend stored.
 *
 * The stored record becomes the `saved` snapshot. Live values stay the
 * author's except where the save changed a file under them: a moved upload
 * takes its permanent URL and is re-sent to the engine, a dropped upload is
 * cleared in the snapshot too, a re-signed URL is adopted.
 *
 * @param params.roomId - Room the save is addressed to.
 * @param params.brandingId - Branding the entity belongs to.
 * @param params.entity - The entity to save.
 * @param params.sendMessageToUnreal - Bridge to the engine.
 * @param params.upsertRealtimeEntity - Registry writer.
 * @param params.publishProperties - Receives the live values that changed.
 * @returns The live values the save changed, by property name.
 * @throws Whatever the save request rejects with; nothing is adopted then.
 */
export const persistRealtimeEntity = async ({
  roomId,
  brandingId,
  entity,
  sendMessageToUnreal,
  upsertRealtimeEntity,
  publishProperties,
}: PersistRealtimeEntityParams): Promise<Record<string, string>> => {
  const snapshot = buildRealtimeEntitySnapshot(entity);
  const record = await SpawnedEntitiesService.saveSpawnedEntity(
    roomId,
    brandingId,
    snapshot,
  );

  const sent = snapshot.properties ?? {};
  const stored = record.properties ?? (snapshot.properties ? {} : undefined);
  const cleared = collectDroppedUploads(sent, stored);
  const resent = { ...cleared, ...collectRelocatedProperties(sent, stored) };
  const adjusted = { ...collectResignedProperties(sent, stored), ...resent };
  const adopted: IRealtimeEntity = {
    ...entity,
    ...(stored ? { properties: { ...stored, ...cleared } } : {}),
  };

  upsertRealtimeEntity({
    entityId: entity.entityId,
    ...(Object.keys(adjusted).length ? { properties: adjusted } : {}),
    saved: buildRealtimeEntitySnapshot(adopted),
  });

  if (Object.keys(resent).length > 0) {
    updateRealtimeEntityInUnreal(sendMessageToUnreal, {
      entityId: entity.entityId,
      properties: resent,
    });
  }

  if (Object.keys(adjusted).length > 0) {
    publishProperties?.(entity.entityId, adjusted);
  }

  return adjusted;
};
